import {
  Button,
  ButtonsGroup,
  Modal,
  ModalProps,
  NavItem,
  notify,
  Select,
  Space,
  Text,
  useModal,
} from '@billy/design-system'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useVatDeclarationPeriods } from '@features/vatDeclarations/hooks/useVatDeclarationPeriods'
import { getVatDeclarationPeriodItems } from '@features/vatDeclarations/utils/getVatDeclarationPeriodItems'

import { queryClient } from '../../../../config/queryClient'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { APIError } from '../../../../utils'
import { useVatDeclarationsRoutes } from '../../context/vatDeclarationsRouteContext'
import {
  ChangeVatDeclarationPeriodPayload,
  useChangeVatDeclarationPeriod,
} from '../../hooks/useChangeVatDeclarationPeriod'
import { PeriodData } from '../../utils/getPeriodData'

interface VatDeclarationsChangePeriodModalProps extends ModalProps {
  currentPeriodType?: string
  vatDeclarationId: string
  periodData?: PeriodData
}

export const VatDeclarationsChangePeriodModal = ({
  currentPeriodType,
  vatDeclarationId,
  id,
  periodData,
}: VatDeclarationsChangePeriodModalProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const navigate = useNavigate()
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriodType)
  const { vatDeclarationPeriods, isLoading: isLoadingPeriods } = useVatDeclarationPeriods()
  const { close: closeModal } = useModal(id)
  const vatDeclarationsRoutes = useVatDeclarationsRoutes()
  const { url: vatDeclarationsUrl } = useOrganizationViewUrl(vatDeclarationsRoutes.getMainRoute())

  const { changePeriod, isProcessing } = useChangeVatDeclarationPeriod({
    onError: (error?: APIError) => {
      notify({
        id: NotificationKeys.VatDeclarationsChangePeriod,
        message: error?.validationErrors?.periodChange?.message || t('vat_declarations.change_period_modal.fail'),
        variant: 'error',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.VatDeclarations)
      notify({
        id: NotificationKeys.VatDeclarationsChangePeriod,
        message: t('vat_declarations.change_period_modal.success'),
        variant: 'success',
      })
      track('VAT Declaration Period Changed (Client)', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        from_period: currentPeriodType,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        to_period: selectedPeriod,
        year: periodData?.year,
      })
      closeModal()
      navigate(vatDeclarationsUrl || '/')
    },
  })

  const vatDeclarationItems: NavItem[] = useMemo(
    () => getVatDeclarationPeriodItems(vatDeclarationPeriods),
    [vatDeclarationPeriods],
  )

  const selectNewPeriod = useCallback(
    (id?: string) => {
      setSelectedPeriod(id)
    },
    [setSelectedPeriod],
  )

  const handleChangePeriodClick = useCallback(() => {
    if (!selectedPeriod) {
      return
    }

    const changePeriodPayload: ChangeVatDeclarationPeriodPayload = {
      vatDeclarationId,
      payload: {
        targetSalesTaxPeriod: selectedPeriod,
      },
    }

    changePeriod(changePeriodPayload)
  }, [changePeriod, selectedPeriod, vatDeclarationId])

  return (
    <Modal id={id} closable size="s">
      <Modal.Header title={t('vat_declarations.change_period_modal.title')} />
      <Modal.Body>
        <Text>{t('vat_declarations.change_period_modal.body')}</Text>
        <Space size="s8" />
        <Text colorVariant="secondary">{t('vat_declarations.change_period_modal.subtitle')}</Text>
        <Select
          selectedId={selectedPeriod}
          items={vatDeclarationItems}
          onSelect={selectNewPeriod}
          dropdownSize="fitTrigger"
          dropdownFetching={isLoadingPeriods}
        />
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button variant="text" onClick={closeModal}>
            {t('cancel')}
          </Button>
          <Button onClick={handleChangePeriodClick} loading={isProcessing}>
            {t('save')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
